// Rate Matrix Utilities for Interest Rate Configuration
// Handles different loan type calculations and rate matrix management

import type { RateProgram } from '@/apis/extended/admin/interestRateApi';

export interface RateMatrix {
  id: string;
  loanType: LoanType;
  matrix: RateCell[][];
  config?: RateTableConfig; // Config for matrix lookup (xAxis/yAxis ranges)
  lastUpdated: string;
  reason?: string;
}

export interface RateCell {
  rowIndex: number;
  colIndex: number;
  rate: number;
  isEditable: boolean;
}

export interface RateTableConfig {
  xAxis: RateAxis;
  yAxis: RateAxis;
  defaultRates: number[][];
}

export interface RateAxis {
  label: string;
  values: string[];
  ranges?: string[];
}

export type LoanType =
  | 'Cash'
  | 'DSCR'
  | 'Conventional'
  | 'FHA'
  | 'USDA'
  | 'VA'
  | 'Commercial'
  | 'FixAndFlip'
  | 'Bridge'
  | 'HELOC'
  | 'HELOAN'
  | 'SecondMortgage';

export interface DTIBracket {
  minDTI: number;
  maxDTI: number;
  rate: number;
}

export interface CashFlowBracket {
  minCoverage: number;
  maxCoverage: number;
  ltvRanges: LTVRange[];
}

export interface LTVRange {
  minLTV: number;
  maxLTV: number;
  rate: number;
}

// Default rate table configurations for each loan type
export const RATE_TABLE_CONFIGS: Record<LoanType, RateTableConfig> = {
  Cash: {
    xAxis: {
      label: 'Cash',
      values: [],
      ranges: [],
    },
    yAxis: {
      label: 'Cash',
      values: [],
      ranges: [],
    },
    defaultRates: [],
  },
  DSCR: {
    xAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (750+)',
        'Good (700-749)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['750+', '700-749', '650-699', '<650'],
    },
    yAxis: {
      label: 'Loan-to-Value (LTV)',
      values: [
        'Low (≤60%)',
        'Moderate (61-80%)',
        'High (81-90%)',
        'Very High (91%+)',
      ],
      ranges: ['≤60%', '61-80%', '81-90%', '91%+'],
    },
    defaultRates: [
      [6.25, 6.5, 6.75, 7.0], // Excellent Credit
      [6.5, 6.75, 7.0, 7.25], // Good Credit
      [6.75, 7.0, 7.25, 7.5], // Fair Credit
      [7.0, 7.25, 7.5, 7.75], // Poor Credit
    ],
  },
  Conventional: {
    xAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (760+)',
        'Good (700-759)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['760+', '700-759', '650-699', '<650'],
    },
    yAxis: {
      label: 'Loan-to-Value (LTV)',
      values: [
        'Low (≤60%)',
        'Moderate (61-80%)',
        'High (81-90%)',
        'Very High (91%+)',
      ],
      ranges: ['≤60%', '61-80%', '81-90%', '91%+'],
    },
    defaultRates: [
      [6.0, 6.25, 6.5, 6.75], // Excellent Credit
      [6.25, 6.5, 6.75, 7.0], // Good Credit
      [6.5, 6.75, 7.0, 7.25], // Fair Credit
      [6.75, 7.0, 7.25, 7.5], // Poor Credit
    ],
  },
  FHA: {
    xAxis: {
      label: 'Debt-to-Income (DTI)',
      values: [
        'Low (≤36%)',
        'Moderate (37-43%)',
        'High (44-50%)',
        'Very High (51%+)',
      ],
      ranges: ['≤36%', '37-43%', '44-50%', '51%+'],
    },
    yAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (760+)',
        'Good (700-759)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['760+', '700-759', '650-699', '<650'],
    },
    defaultRates: [
      [5.75, 6.0, 6.25, 6.5], // Low DTI
      [6.0, 6.25, 6.5, 6.75], // Moderate DTI
      [6.25, 6.5, 6.75, 7.0], // High DTI
      [6.5, 6.75, 7.0, 7.25], // Very High DTI
    ],
  },
  USDA: {
    xAxis: {
      label: 'Debt-to-Income (DTI)',
      values: [
        'Low (≤36%)',
        'Moderate (37-43%)',
        'High (44-50%)',
        'Very High (51%+)',
      ],
      ranges: ['≤36%', '37-43%', '44-50%', '51%+'],
    },
    yAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (760+)',
        'Good (700-759)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['760+', '700-759', '650-699', '<650'],
    },
    defaultRates: [
      [5.5, 5.75, 6.0, 6.25], // Low DTI
      [5.75, 6.0, 6.25, 6.5], // Moderate DTI
      [6.0, 6.25, 6.5, 6.75], // High DTI
      [6.25, 6.5, 6.75, 7.0], // Very High DTI
    ],
  },
  VA: {
    xAxis: {
      label: 'Debt-to-Income (DTI)',
      values: [
        'Low (≤36%)',
        'Moderate (37-43%)',
        'High (44-50%)',
        'Very High (51%+)',
      ],
      ranges: ['≤36%', '37-43%', '44-50%', '51%+'],
    },
    yAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (760+)',
        'Good (700-759)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['760+', '700-759', '650-699', '<650'],
    },
    defaultRates: [
      [5.25, 5.5, 5.75, 6.0], // Low DTI
      [5.5, 5.75, 6.0, 6.25], // Moderate DTI
      [5.75, 6.0, 6.25, 6.5], // High DTI
      [6.0, 6.25, 6.5, 6.75], // Very High DTI
    ],
  },
  Commercial: {
    xAxis: {
      label: 'Cash Flow Coverage Ratio',
      values: [
        'Excellent (≥1.5)',
        'Good (1.25-1.49)',
        'Fair (1.1-1.24)',
        'Poor (<1.1)',
      ],
      ranges: ['≥1.5', '1.25-1.49', '1.1-1.24', '<1.1'],
    },
    yAxis: {
      label: 'Loan-to-Value (LTV)',
      values: [
        'Low (≤65%)',
        'Moderate (66-75%)',
        'High (76-85%)',
        'Very High (86%+)',
      ],
      ranges: ['≤65%', '66-75%', '76-85%', '86%+'],
    },
    defaultRates: [
      [6.5, 6.75, 7.0, 7.25], // Excellent Coverage
      [6.75, 7.0, 7.25, 7.5], // Good Coverage
      [7.0, 7.25, 7.5, 7.75], // Fair Coverage
      [7.25, 7.5, 7.75, 8.0], // Poor Coverage
    ],
  },
  FixAndFlip: {
    xAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (760+)',
        'Good (700-759)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['760+', '700-759', '650-699', '<650'],
    },
    yAxis: {
      label: 'Loan-to-Value (LTV)',
      values: [
        'Low (≤60%)',
        'Moderate (61-80%)',
        'High (81-90%)',
        'Very High (91%+)',
      ],
      ranges: ['≤60%', '61-80%', '81-90%', '91%+'],
    },
    defaultRates: [
      [8.0, 8.25, 8.5, 8.75], // Low LTV
      [8.25, 8.5, 8.75, 9.0], // Moderate LTV
      [8.5, 8.75, 9.0, 9.25], // High LTV
      [8.75, 9.0, 9.25, 9.5], // Very High LTV
    ],
  },

  Bridge: {
    xAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (760+)',
        'Good (700-759)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['760+', '700-759', '650-699', '<650'],
    },
    yAxis: {
      label: 'Loan-to-Value (LTV)',
      values: [
        'Low (≤60%)',
        'Moderate (61-80%)',
        'High (81-90%)',
        'Very High (91%+)',
      ],
      ranges: ['≤60%', '61-80%', '81-90%', '91%+'],
    },
    defaultRates: [
      [9.0, 9.25, 9.5, 9.75],
      [9.25, 9.5, 9.75, 10.0],
      [9.5, 9.75, 10.0, 10.25],
      [9.75, 10.0, 10.25, 10.5],
    ],
  },

  HELOC: {
    xAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (760+)',
        'Good (700-759)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['760+', '700-759', '650-699', '<650'],
    },
    yAxis: {
      label: 'Combined Loan-to-Value (CLTV)',
      values: [
        'Low (≤60%)',
        'Moderate (61-80%)',
        'High (81-90%)',
        'Very High (91%+)',
      ],
      ranges: ['≤60%', '61-80%', '81-90%', '91%+'],
    },
    defaultRates: [
      [7.5, 7.75, 8.0, 8.25],
      [7.75, 8.0, 8.25, 8.5],
      [8.0, 8.25, 8.5, 8.75],
      [8.25, 8.5, 8.75, 9.0],
    ],
  },

  HELOAN: {
    xAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (760+)',
        'Good (700-759)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['760+', '700-759', '650-699', '<650'],
    },
    yAxis: {
      label: 'Combined Loan-to-Value (CLTV)',
      values: [
        'Low (≤60%)',
        'Moderate (61-80%)',
        'High (81-90%)',
        'Very High (91%+)',
      ],
      ranges: ['≤60%', '61-80%', '81-90%', '91%+'],
    },
    defaultRates: [
      [7.0, 7.25, 7.5, 7.75],
      [7.25, 7.5, 7.75, 8.0],
      [7.5, 7.75, 8.0, 8.25],
      [7.75, 8.0, 8.25, 8.5],
    ],
  },

  SecondMortgage: {
    xAxis: {
      label: 'Credit Score',
      values: [
        'Excellent (760+)',
        'Good (700-759)',
        'Fair (650-699)',
        'Poor (<650)',
      ],
      ranges: ['760+', '700-759', '650-699', '<650'],
    },
    yAxis: {
      label: 'Combined Loan-to-Value (CLTV)',
      values: [
        'Low (≤60%)',
        'Moderate (61-80%)',
        'High (81-90%)',
        'Very High (91%+)',
      ],
      ranges: ['≤60%', '61-80%', '81-90%', '91%+'],
    },
    defaultRates: [
      [8.0, 8.25, 8.5, 8.75],
      [8.25, 8.5, 8.75, 9.0],
      [8.5, 8.75, 9.0, 9.25],
      [8.75, 9.0, 9.25, 9.5],
    ],
  },
};

// Create a new rate matrix for a loan type
export const createRateMatrix = (
  loanType: LoanType,
  reason?: string
): RateMatrix => {
  const config = RATE_TABLE_CONFIGS[loanType];
  const matrix: RateCell[][] = [];

  for (let i = 0; i < config.defaultRates.length; i++) {
    matrix[i] = [];
    for (let j = 0; j < config.defaultRates[i].length; j++) {
      matrix[i][j] = {
        rowIndex: i,
        colIndex: j,
        rate: config.defaultRates[i][j],
        isEditable: true,
      };
    }
  }

  return {
    id: `${loanType.toLowerCase()}_${Date.now()}`,
    loanType,
    matrix,
    config, // Include config for matrix lookup
    lastUpdated: new Date().toISOString(),
    reason,
  };
};

// Update a specific cell in the rate matrix
export const updateRateCell = (
  matrix: RateMatrix,
  rowIndex: number,
  colIndex: number,
  newRate: number
): RateMatrix => {
  const updatedMatrix = matrix.matrix.map((row, i) =>
    row.map((cell, j) =>
      i === rowIndex && j === colIndex ? { ...cell, rate: newRate } : cell
    )
  );

  return {
    ...matrix,
    matrix: updatedMatrix,
    lastUpdated: new Date().toISOString(),
  };
};

// Get rate for specific criteria
export const getRateForCriteria = (
  matrix: RateMatrix,
  xValue: string,
  yValue: string
): number | null => {
  const config = RATE_TABLE_CONFIGS[matrix.loanType];

  const xIndex = config.xAxis.values.findIndex((val) => val.includes(xValue));
  const yIndex = config.yAxis.values.findIndex((val) => val.includes(yValue));

  if (xIndex === -1 || yIndex === -1) {
    return null;
  }

  return matrix.matrix[yIndex][xIndex].rate;
};

// Validate rate matrix
export const validateRateMatrix = (
  matrix: RateMatrix
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (matrix.loanType === 'Cash') {
    return { isValid: true, errors };
  }

  if (!matrix.matrix || matrix.matrix.length === 0) {
    errors.push('Matrix is empty');
    return { isValid: false, errors };
  }

  // Check for reasonable rate ranges
  const minRate = 2.0; // 2% minimum
  const maxRate = 15.0; // 15% maximum

  for (let i = 0; i < matrix.matrix.length; i++) {
    for (let j = 0; j < matrix.matrix[i].length; j++) {
      const cell = matrix.matrix[i][j];
      if (cell.rate < minRate || cell.rate > maxRate) {
        errors.push(
          `Rate at position [${i}][${j}] (${cell.rate}%) is outside valid range (${minRate}%-${maxRate}%)`
        );
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Calculate weighted average rate for a matrix
export const calculateAverageRate = (matrix: RateMatrix): number => {
  let totalRate = 0;
  let totalCells = 0;

  for (const row of matrix.matrix) {
    for (const cell of row) {
      totalRate += cell.rate;
      totalCells++;
    }
  }

  return totalCells > 0 ? totalRate / totalCells : 0;
};

// Export default rate matrices for all loan types
export const getDefaultRateMatrices = (): Record<LoanType, RateMatrix> => {
  const matrices: Partial<Record<LoanType, RateMatrix>> = {};

  (Object.keys(RATE_TABLE_CONFIGS) as LoanType[]).forEach((loanType) => {
    matrices[loanType] = createRateMatrix(loanType, 'Default configuration');
  });

  return matrices as Record<LoanType, RateMatrix>;
};

// Map backend program keys to frontend LoanType
export const BACKEND_TO_FRONTEND_LOAN_TYPE: Record<string, LoanType> = {
  cash: 'Cash',
  conventional: 'Conventional',
  fha: 'FHA',
  va: 'VA',
  usda: 'USDA',
  dscr: 'DSCR',
  commercial: 'Commercial',
  fixAndFlip: 'FixAndFlip',
  bridge: 'Bridge',
  heloc: 'HELOC',
  heloan: 'HELOAN',
  secondMortgage: 'SecondMortgage',
};

export const FRONTEND_TO_BACKEND_LOAN_TYPE: Record<LoanType, string> = {
  Cash: 'cash',
  Conventional: 'conventional',
  FHA: 'fha',
  VA: 'va',
  USDA: 'usda',
  DSCR: 'dscr',
  Commercial: 'commercial',
  FixAndFlip: 'fixAndFlip',
  Bridge: 'bridge',
  HELOC: 'heloc',
  HELOAN: 'heloan',
  SecondMortgage: 'secondMortgage',
};

// Transform backend rate matrix structure to frontend format
export const transformBackendMatrixToFrontend = (
  loanType: LoanType,
  backendMatrix: {
    values: number[][];
    rowLabels: string[];
    columnLabels: string[];
    lastUpdated?: string | null;
  }
): RateMatrix => {
  const config = RATE_TABLE_CONFIGS[loanType];

  // Convert backend values (2D number array) to frontend RateCell[][]
  const matrix: RateCell[][] = [];

  if (backendMatrix.values && Array.isArray(backendMatrix.values)) {
    for (let i = 0; i < backendMatrix.values.length; i++) {
      matrix[i] = [];
      if (Array.isArray(backendMatrix.values[i])) {
        for (let j = 0; j < backendMatrix.values[i].length; j++) {
          matrix[i][j] = {
            rowIndex: i,
            colIndex: j,
            rate: backendMatrix.values[i][j] || 0,
            isEditable: true,
          };
        }
      }
    }
  }

  // Extract ranges from backend labels (e.g., "Low (≤60%)" -> "≤60%")
  const extractRange = (label: string): string => {
    // Try to extract range from label like "Low (≤60%)" -> "≤60%"
    const match = label.match(/\(([^)]+)\)/);
    if (match) return match[1];
    // If no parentheses, try to extract range directly
    if (
      label.includes('≤') ||
      label.includes('≥') ||
      label.includes('+') ||
      label.includes('-')
    ) {
      return label;
    }
    return label;
  };

  // Use backend labels if available, otherwise fall back to config defaults
  const xAxisRanges = backendMatrix.columnLabels
    ? backendMatrix.columnLabels.map(extractRange)
    : config.xAxis.ranges || config.xAxis.values;
  const yAxisRanges = backendMatrix.rowLabels
    ? backendMatrix.rowLabels.map(extractRange)
    : config.yAxis.ranges || config.yAxis.values;

  // Create transformed matrix with config
  return {
    id: `${loanType.toLowerCase()}_${Date.now()}`,
    loanType,
    matrix,
    config: {
      xAxis: {
        ...config.xAxis,
        ranges: xAxisRanges,
      },
      yAxis: {
        ...config.yAxis,
        ranges: yAxisRanges,
      },
      defaultRates: config.defaultRates,
    },
    lastUpdated: backendMatrix.lastUpdated || new Date().toISOString(),
    reason: 'Loaded from backend',
  };
};

// Transform all backend rate programs to frontend format
export const transformAllBackendMatrices = (backendRates: {
  programs: Partial<Record<string, RateProgram>>;
}): Partial<Record<LoanType, RateMatrix>> => {
  const matrices: Partial<Record<LoanType, RateMatrix>> = {};

  Object.entries(BACKEND_TO_FRONTEND_LOAN_TYPE).forEach(
    ([backendKey, frontendLoanType]) => {
      const program = backendRates.programs[backendKey];
      if (program?.matrix?.values && Array.isArray(program.matrix.values)) {
        matrices[frontendLoanType] = transformBackendMatrixToFrontend(
          frontendLoanType,
          {
            values: program.matrix.values,
            rowLabels: program.matrix.rowLabels || [],
            columnLabels: program.matrix.columnLabels || [],
            lastUpdated: program.matrix.lastUpdated,
          }
        );
      }
    }
  );

  return matrices;
};
